import moment from "moment";

/**
 * Pure utility: segments multi-day processes safely
 */
export function segmentProcesses(datesArray) {
  if (!Array.isArray(datesArray) || datesArray.length === 0) {
    return datesArray;
  }

  const calendar = JSON.parse(JSON.stringify(datesArray));
  const seenProcesses = new Map();

  const isNonWorkingDay = (day) =>
    day.dayOff || day.holiday?.status === "Day Off";

  /**
   * Normalize start day → first working day
   */
  const getFirstWorkingDayIndex = (startIndex) => {
    let i = startIndex;
    while (i < calendar.length && isNonWorkingDay(calendar[i])) {
      i++;
    }
    return i;
  };

  /**
   * Split width into segments, breaking on holidays
   * Each segment spans contiguous working days
   */
  const createSegments = (startIndex, width) => {
    const segments = [];
    let remaining = width;
    let cursor = startIndex;

    while (remaining > 0 && cursor < calendar.length) {
      // Skip non-working days
      while (cursor < calendar.length && isNonWorkingDay(calendar[cursor])) {
        cursor++;
      }

      if (cursor >= calendar.length) break;

      const segmentStart = cursor;
      let segmentWidth = 0;

      // Grow segment across contiguous working days
      while (
        cursor < calendar.length &&
        !isNonWorkingDay(calendar[cursor]) &&
        remaining > 0
      ) {
        segmentWidth++;
        remaining--;
        cursor++;
      }

      if (segmentWidth > 0) {
        segments.push({
          startIndex: segmentStart,
          width: segmentWidth
        });
      }
    }

    return segments;
  };

  /**
   * PASS 1 — collect unique processes by order_id + process_id
   * Group all instances and keep the earliest start with full width
   */
  const processGroups = new Map(); // Map<key, Array<{dayIndex, dataItem, processData}>>

  calendar.forEach((day, dayIndex) => {
    day.dayData?.forEach((dataItem) => {
      dataItem.value?.forEach((processData) => {
        const proc = processData.proccess;
        if (!proc || proc.backlog) return;

        // Use process_id for grouping (not _id which might be instance-specific)
        const key = `${processData.order_id}_${proc.process_id}`;

        if (!processGroups.has(key)) {
          processGroups.set(key, []);
        }

        processGroups.get(key).push({
          dayIndex,
          dataItem,
          processData
        });
      });
    });
  });

  // For each group, find the instance with earliest start date and largest width
  processGroups.forEach((instances, key) => {
    if (instances.length === 0) return;

    // Sort by: earliest start date, then largest width
    instances.sort((a, b) => {
      const aStart = moment(a.processData.proccess.process_date);
      const bStart = moment(b.processData.proccess.process_date);
      const dateDiff = aStart.diff(bStart);
      
      if (dateDiff !== 0) return dateDiff; // Earlier start wins
      
      // If same start date, larger width wins
      const aWidth = a.processData.proccess.process_width || 1;
      const bWidth = b.processData.proccess.process_width || 1;
      return bWidth - aWidth;
    });

    // Keep the first (best) instance, mark others for removal
    const [best, ...duplicates] = instances;
    seenProcesses.set(key, best);

    duplicates.forEach(dup => {
      dup.processData._remove = true;
    });
  });

  /**
   * PASS 2 — segment processes (REMOVE originals) + TRACK PROCESS ORDER
   */
  const additions = [];
  const processFirstAppearance = new Map(); // Track when each process first appears

  for (const { dayIndex, dataItem, processData } of seenProcesses.values()) {
    const proc = processData.proccess;
    const width = proc.process_width || 1;
    
    // Create unique process identifier
    const processId = `${processData.order_id}_${proc.process_id}`;

    // ❗ always remove original
    processData._remove = true;

    if (width <= 1) {
      // single-day process → only add if working day
      const start = getFirstWorkingDayIndex(dayIndex);
      if (start < calendar.length) {
        // Track first appearance
        if (!processFirstAppearance.has(processId)) {
          processFirstAppearance.set(processId, start);
        }
        
        additions.push({
          dayIndex: start,
          dataItem,
          processData: {
            ...processData,
            proccess: {
              ...proc,
              processId: processId
            },
            process: {
              ...proc,
              processId: processId
            }
          }
        });
      }
      continue;
    }

    const normalizedStart = getFirstWorkingDayIndex(dayIndex);
    if (normalizedStart >= calendar.length) continue;

    const segments = createSegments(normalizedStart, width);
    
    // Track first appearance (use the first segment's first day)
    if (segments.length > 0 && !processFirstAppearance.has(processId)) {
      processFirstAppearance.set(processId, segments[0].startIndex);
    }

    // For each segment, create individual day entries
    segments.forEach((seg, segIdx) => {
      // Create entries for each day in this segment
      for (let dayOffset = 0; dayOffset < seg.width; dayOffset++) {
        // ✅ FIX: First day of EACH segment is isFirstSegment
        const isFirstDayOfSegment = dayOffset === 0;
        
        const clonedProc = {
          ...proc,
          _id: (segIdx === 0 && dayOffset === 0) ? proc._id : `${proc._id}_seg_${segIdx}_day_${dayOffset}`,
          process_width: 1, // ✅ Always 1 - each entry renders only its own day
          original_process_width: seg.width, // Store original for reference
          isFirstSegment: isFirstDayOfSegment, // ✅ True for first day of each segment
          isSegment: !isFirstDayOfSegment, // ✅ False for first day of each segment
          segmentIndex: segIdx,
          dayOffsetInSegment: dayOffset,
          totalSegments: segments.length,
          processId: processId
        };

        additions.push({
          dayIndex: seg.startIndex + dayOffset,
          dataItem,
          processData: {
            ...processData,
            proccess: clonedProc,
            process: clonedProc
          }
        });
      }
    });
  }

  /**
   * PASS 3 — cleanup removed
   */
  calendar.forEach((day) => {
    day.dayData?.forEach((item) => {
      item.value = item.value?.filter((p) => !p._remove);
    });
  });

  /**
   * PASS 4 — inject segments
   */
  additions.forEach(({ dayIndex, dataItem, processData }) => {
    const day = calendar[dayIndex];
    const lane = day.dayData?.find((d) => d.key === dataItem.key);
    if (lane) {
      lane.value.push(processData);
    }
  });

  /**
   * PASS 5 — detect resource conflicts + ASSIGN SORT KEYS WITH GAP FILLING
   */
  const processIdToSortKey = new Map();
  
  calendar.forEach((day, dayIndex) => {
    day.dayData?.forEach((lane) => {
      // Collect process IDs on this day
      const processIdsOnThisDay = new Set();
      lane.value?.forEach(p => {
        const processId = p.proccess?.processId;
        if (processId) {
          processIdsOnThisDay.add(processId);
        }
      });
      
      // Find which sort keys are already taken by existing processes
      const takenSortKeys = new Set();
      processIdsOnThisDay.forEach(processId => {
        if (processIdToSortKey.has(processId)) {
          takenSortKeys.add(processIdToSortKey.get(processId));
        }
      });
      
      // Assign sort keys to new processes, filling gaps
      let nextSortKey = 1;
      processIdsOnThisDay.forEach(processId => {
        if (!processIdToSortKey.has(processId)) {
          // Find the smallest available sort key
          while (takenSortKeys.has(nextSortKey)) {
            nextSortKey++;
          }
          processIdToSortKey.set(processId, nextSortKey);
          takenSortKeys.add(nextSortKey);
          nextSortKey++;
        }
      });

      // Apply sort keys to all processes
      lane.value?.forEach(p => {
        const processId = p.proccess?.processId;
        if (processId && processIdToSortKey.has(processId)) {
          p.proccess.sortKey = processIdToSortKey.get(processId);
          if (p.process) {
            p.process.sortKey = processIdToSortKey.get(processId);
          }
        }
      });

      // Detect conflicts
      const nonSegments = lane.value?.filter(p => !p.proccess?.isSegment) || [];
      
      // If multiple non-segment processes on same day/resource = conflict
      if (nonSegments.length > 1) {
        nonSegments.forEach(p => {
          p.proccess.hasConflict = true;
          p.proccess.conflictCount = nonSegments.length;
        });
      }
    });
  });

  return calendar;
}